'use client';

import React, { useContext, useState, forwardRef, useEffect } from 'react';
import { useTranslation } from '@/app/i18n/client';
import languageContext from '@/context/language';
import { Table, Tooltip } from '@/shared/components';
import OrderDetails from './mini-order-details';
import { Order, Page } from '@/types/orderhistory';
import '../index.css';
import { iconClass, iconColor } from '../constant';
import Link from 'next/link';
import dayjs from 'dayjs';
import { TableColumn } from '@/shared/components/Table/use-column';
import { usePriceFormat } from '@/hooks/use-price-format';
import { CURRENCY, ORDER_SOURCE, ORDER_STATUS, mapOrderStatusKey } from '@/app/utils/constant';
import { useCountry } from '@/hooks/use-country';
import { NAMESPACE, useLang } from '@/hooks/use-lang';

type Value = Object;
interface IProps {
  data: Array<Order>;
  page: Page;
  updatePageNumber: (value: Value) => void;
  updatePageSize: any;
  sortFunc: (value: Value) => void;
  sorting: Array<[]>;
  filterFunc: (value: Value) => void;
  columnFilters: Array<[]>;
}

const OrderHistoryTable = forwardRef<HTMLInputElement, IProps>(function OrderHistoryTable(props, ref) {
  const { data, page, updatePageNumber, updatePageSize, sortFunc, sorting, filterFunc, columnFilters } = props;
  const { lang } = useContext(languageContext);
  const { t: commonT } = useLang(NAMESPACE.common);
  const { t } = useTranslation(lang, 'orderhistory');
  const { t: dispatchT } = useLang(NAMESPACE.dispatchNote);
  const { t: invoiceT } = useLang(NAMESPACE.invoice);
  const [expanded, setExpanded] = useState({});
  const getFormattedPrice = usePriceFormat();
  const { isCN, isKR } = useCountry();

  const orderReferenceColumn: TableColumn<any> = {
    accessorKey: 'orderReferenceNo',
    header: t('order.reference.number'),
    sorter: true,
    size: lang === 'en' ? 200 : 115,
  };

  const orderNumberColumn: TableColumn<any> = {
    accessorKey: 'orderNumber',
    header: t('order.number'),
    sorter: true,
    size: 130,
    cell: (row) => {
      return row.orderNumber ? (
        <Link
          prefetch
          href={{
            pathname: '/order-detail',
            query: {
              orderNumber: row.orderNumber,
              webOrderNumber: row.orderReferenceNo,
            },
          }}
        >
          {row.orderNumber}
        </Link>
      ) : (
        <div className="flex">-</div>
      );
    },
  };

  const columns: TableColumn<any>[] = [
    {
      accessorKey: 'orderDate',
      header: t('order.date'),
      sorter: true,
      cell: (row) => {
        const dateVal = row.orderDate;
        const date = dateVal ? dayjs(dateVal).format('DD-MMM-YYYY') : '';
        return date;
      },
      size: 120,
    },
    ...(isKR ? [orderNumberColumn, orderReferenceColumn] : [orderReferenceColumn, orderNumberColumn]),
    {
      accessorKey: 'poNumber',
      header: t('purchase.order.number'),
      sorter: true,
      size: 200,
    },
    {
      accessorKey: 'orderSource',
      header: t('order.source'),
      filters: ORDER_SOURCE,
      filterMultiple: true,
      size: 120,
      onFilter: (row, columnId, filterValue) => {
        return true;
      },
    },
    {
      accessorKey: 'amtTotalPrice',
      header: t('total') + ` (${CURRENCY})`,
      sorter: true,
      size: 120,
      cell: (row) => {
        return <div className="flex">{getFormattedPrice(row.amtTotalPrice)}</div>;
      },
    },
    {
      accessorKey: 'loginEmail',
      header: t('created.by'),
      enableHiding: isKR,
      style: {
        minWidth: '200px',
      },
    },
    {
      accessorKey: 'orderStatus',
      header: t('status'),
      filters: ORDER_STATUS.map((item) => ({ label: commonT(item.key), value: item.value })),
      filterMultiple: true,
      style: {
        minWidth: '200px',
      },
      cell: (row) => {
        const colorClass = iconColor[row.orderStatus] ? iconColor[row.orderStatus] : 'text-[#1e8ae7]';
        return (
          <>
            <Tooltip title={isCN && row.orderStatus === 'Pending Review' && row.holdReason} placement="top">
              <div className="flex">
                <span className={`p-1 ${colorClass} ${iconClass[row.orderStatus]}`}></span>
                <span>{commonT(mapOrderStatusKey(row.orderStatus))}</span>
              </div>
            </Tooltip>
          </>
        );
      },
      onFilter: (row, columnId, filterValue) => {
        return true;
      },
    },
  ];
  const expandable = {
    rowExpandable: (row) => {
      return true;
    },
    expandedRowRender: (row, expanded) => (
      <>
        {expanded && (
          <OrderDetails rowContent={row} orderStatus={row.orderStatus} dispatchT={dispatchT} invoiceT={invoiceT} />
        )}
      </>
    ),
  };

  const expandFunc = (props) => {
    setExpanded(props);
  };

  const updatePageNumberFunc = (e) => {
    updatePageNumber(e);
    setExpanded({});
  };

  useEffect(() => {
    setExpanded({});
  }, [props.data]);

  return (
    <Table
      size="large"
      columns={columns}
      dataSource={data}
      pageData={page}
      expandable={expandable}
      className="w-full order-list-table min-w-[1300px]"
      updatePageNumber={updatePageNumberFunc}
      updatePageSize={updatePageSize}
      sorting={sorting}
      setSorting={sortFunc}
      setColumnFilters={filterFunc}
      columnFilters={columnFilters}
      expanded={expanded}
      setExpanded={expandFunc}
      noDataDes={t('no.order.found')}
      manualSorting={true}
    ></Table>
  );
});
export { OrderHistoryTable };
